// Thin wrapper around `qrcode-generator` (MIT, 0 deps) that draws a QR into
// a <canvas>. The library is BUNDLED into the JS build — there is NEVER a
// network call to any CDN or external API when generating the QR.
//
// `qrcode-generator` by Kazuhiko Arase — https://github.com/kazuhikoarase/qrcode-generator
// License: MIT. Pure JS, zero dependencies.

import qrcode from "qrcode-generator";

/**
 * Draw a QR code encoding `text` into the given <canvas>. The canvas is
 * resized to `size`×`size` CSS pixels (and `size*dpr` physical pixels for
 * crisp rendering on hi-DPI). Colors are plain hex strings.
 *
 * No HTTP requests are made.
 */
export function drawQRCodeToCanvas(canvas, text, {
  size = 220,
  level = "M",
  fg = "#0F172A",
  bg = "#FFFFFF",
  padding = 4, // in modules (QR "quiet zone")
} = {}) {
  if (!canvas || typeof canvas.getContext !== "function") return false;
  if (!text) return false;
  const qr = qrcode(0, level);
  qr.addData(String(text));
  qr.make();

  const modules = qr.getModuleCount(); // e.g., 21 for v1
  const totalModules = modules + padding * 2;
  const dpr = typeof window !== "undefined" ? Math.max(1, window.devicePixelRatio || 1) : 1;
  const cellPx = Math.max(1, Math.floor((size * dpr) / totalModules));
  const canvasPx = cellPx * totalModules;

  canvas.width = canvasPx;
  canvas.height = canvasPx;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;

  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  // Background
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvasPx, canvasPx);
  // Modules
  ctx.fillStyle = fg;
  for (let r = 0; r < modules; r++) {
    for (let c = 0; c < modules; c++) {
      if (qr.isDark(r, c)) {
        const x = (c + padding) * cellPx;
        const y = (r + padding) * cellPx;
        ctx.fillRect(x, y, cellPx, cellPx);
      }
    }
  }
  return true;
}
